import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Bus, Car, Ellipsis, Plane, Ship, TrainFront } from 'lucide-react';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { CountryCitySelector } from '@/components/CountryCitySelector';
import ModernAnnonceCard from '@/components/ModernAnnonceCard';
import { StepBar, StepCard, StepNav, Field, ChoicePills, ChoiceCards, CurrencyPicker, DraftButton, RecapLinks } from '@/components/publish/PublishKit';
import { ccySym } from '@/utils/currencyUtils';
import CurrencyService from '@/services/CurrencyService';
import { calcPriceLimits, ZERO_DEC_CCY } from '@/utils/tripPriceUtils';
import { CONDITION_LABELS, plainLabel } from '@/utils/listingParsers';
import {
  TRIP_STEPS, FLEX_OPTIONS, tripStepErrors, firstInvalidStep, toggleChoice, tripPayload, normalizeFlex,
  readDraft, saveDraft, clearDraft,
} from '@/utils/publishForm';
import { canDo } from '@/utils/verifications';
import VerificationGate from '@/components/verification/VerificationGate';
import '@/styles/pro-upsell.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;
const DRAFT_KEY = 'c2s_draft_trip';

const TRANSPORTS = [
  { id: 'avion', label: 'Avion', icon: Plane },
  { id: 'voiture', label: 'Voiture', icon: Car },
  { id: 'bus', label: 'Bus ou car', icon: Bus },
  { id: 'bateau', label: 'Bateau', icon: Ship },
  { id: 'train', label: 'Train', icon: TrainFront },
  { id: 'autre', label: 'Autre', icon: Ellipsis },
];
// Valeurs enregistrées telles quelles (trajets existants) : ne pas les renommer
const CATEGORIES = ['Vêtements', 'Électronique', 'Alimentaire', 'Documents', 'Médicaments', 'Cosmétiques', 'Livres', 'Autre'];
// Les six plus courantes d'abord ; « Assurance incluse » et « Suivi en temps réel » ne sont plus proposés : la
// plateforme ne fournit ni l'un ni l'autre (décision de l'utilisateur, 2026-11-03)
const CONDITION_ORDER = ['frag', 'scelle', 'double_check', 'pickup', 'airport_only', 'sign', 'temp', 'id_check', 'contactless', 'doc_required'];
const CONDITIONS = CONDITION_ORDER.map((id) => ({ id, label: plainLabel(CONDITION_LABELS[id]) }));

const EMPTY = {
  origCountry: '', origCity: '', destCountry: '', destCity: '', date: '', arrivalDate: '', flex: 'fixe',
  transports: ['avion'], transportCustom: '',
  capacity: '', maxPerParcel: '', maxParcels: '', dimL: '', dimW: '', dimH: '', categories: [], categoryCustom: '',
  mode: 'kg', pricePerKg: '', priceFixed: '', currencies: ['EUR'], primaryCurrency: 'EUR', counterOffers: true,
  description: '', conditions: [], message: '',
};

/** Trajet enregistré (ou dupliqué) → état du formulaire. */
function fromTrip(t, { keepDate }) {
  const [dimL = '', dimW = '', dimH = ''] = (t.max_dimensions || '').replace(' cm', '').split('×').map((x) => x.trim().replace('?', ''));
  const cats = (t.accepted_categories || []).filter((c) => CATEGORIES.includes(c));
  const custom = t.category_custom || '';
  return {
    ...EMPTY,
    origCountry: t.departure_country || '', origCity: t.departure_city || '',
    destCountry: t.arrival_country || '', destCity: t.arrival_city || '',
    date: keepDate ? (t.departure_date?.slice(0, 10) || '') : '', arrivalDate: keepDate ? (t.arrival_date?.slice(0, 10) || '') : '',
    flex: normalizeFlex(t.flex_departure),
    transports: t.transport_modes?.length ? t.transport_modes : ['avion'], transportCustom: t.transport_custom || '',
    capacity: t.available_weight_kg != null ? String(t.available_weight_kg) : '',
    maxPerParcel: t.max_weight_per_parcel != null ? String(t.max_weight_per_parcel) : '',
    maxParcels: t.max_parcels != null ? String(t.max_parcels) : '', dimL, dimW, dimH,
    categories: custom ? [...cats, 'Autre'] : cats, categoryCustom: custom,
    mode: t.pricing_mode || 'kg', pricePerKg: t.price_per_kg && (t.pricing_mode || 'kg') === 'kg' ? String(t.price_per_kg) : '',
    priceFixed: t.price_fixed != null ? String(t.price_fixed) : '',
    currencies: t.currencies?.length ? t.currencies : ['EUR'], primaryCurrency: t.primary_currency || 'EUR',
    counterOffers: t.counter_offers ?? true, description: t.description || '',
    conditions: (t.conditions || []).filter((c) => CONDITION_ORDER.includes(c)), message: t.message_to_senders || '',
  };
}

const fmt = (n, ccy) => Number(n).toLocaleString('fr-FR', { maximumFractionDigits: ZERO_DEC_CCY.has(ccy) ? 0 : 2 });

/**
 * Publier un trajet — parcours en quatre étapes (direction C, planche C-Publier-trajet, 2026-11-03) : le trajet, la
 * place, le prix, les détails (facultatifs), puis le récapitulatif sous forme de carte d'annonce. En miroir du colis.
 */
export default function TripForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const { id } = useParams();
  const isEdit = !!id;
  const isDuplicate = new URLSearchParams(location.search).get('duplicate') === '1';
  // Capacité et plafond de prix : réservés à l'abonnement PRO (pas au type de compte déclaré)
  const isPro = !!user?.pro_subscribed;
  const maxWeight = isPro ? 1500 : 250;

  const [draft] = useState(() => {
    const d = !isEdit && !isDuplicate ? readDraft(DRAFT_KEY) : null;
    return d && (d.origCountry || d.destCountry || d.date || d.capacity) ? { ...EMPTY, ...d } : null;
  });
  const [f, setF] = useState(draft || EMPTY);
  const [draftNote, setDraftNote] = useState(!!draft);
  const [step, setStep] = useState('route');
  const [tried, setTried] = useState({});
  const [fetching, setFetching] = useState(isEdit);
  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState({ desc: false, msg: false });
  const [showDims, setShowDims] = useState(false);
  const [limits, setLimits] = useState(() => calcPriceLimits('EUR', isPro));
  const top = useRef(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const ccy = f.primaryCurrency;
  const sym = ccySym(ccy);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/trips/${id}`)
        // Trajet expiré : le republier demande une nouvelle date (le serveur refuse l'ancienne)
        .then((r) => setF(fromTrip(r.data, { keepDate: r.data.status !== 'EXPIRED' })))
        .catch(() => toast.error('Trajet introuvable'))
        .finally(() => setFetching(false));
    } else if (isDuplicate) {
      try {
        const stored = sessionStorage.getItem('c2s_trip_duplicate');
        if (stored) {
          setF(fromTrip(JSON.parse(stored), { keepDate: false }));
          sessionStorage.removeItem('c2s_trip_duplicate');
          toast.success('Formulaire prérempli — adaptez les informations avant de publier.');
        }
      } catch { /* stockage indisponible */ }
    }
  }, [id, isEdit, isDuplicate, toast]);

  useEffect(() => { if (!isEdit && !isDuplicate) saveDraft(DRAFT_KEY, f); }, [f, isEdit, isDuplicate]);

  // Bornes du tarif au kilo (1 € à 250 €, 1 500 € avec PRO) dans la devise principale : taux de secours d'abord, puis
  // taux du jour s'il répond (le serveur contrôle aussi)
  useEffect(() => {
    setLimits(calcPriceLimits(ccy, isPro));
    if (ccy === 'EUR') return;
    CurrencyService.getRates().then((data) => {
      const rate = data?.rates?.[ccy];
      if (!rate) return;
      const maxEur = isPro ? 1500 : 250;
      setLimits({
        min: ZERO_DEC_CCY.has(ccy) ? Math.ceil(rate) : Math.round(rate * 100) / 100,
        max: ZERO_DEC_CCY.has(ccy) ? Math.floor(rate * maxEur) : Math.round(rate * maxEur * 100) / 100,
      });
    }).catch(() => {});
  }, [ccy, isPro]);

  const euroNote = ccy !== 'EUR' ? ` (≈ 1 à ${isPro ? '1 500' : '250'} €)` : '';
  const ctx = {
    maxWeight, priceMin: limits.min, priceMax: limits.max,
    priceRangeText: `${fmt(limits.min, ccy)} et ${fmt(limits.max, ccy)} ${sym} le kilo${euroNote}`,
    fixedMinText: `${fmt(limits.min, ccy)} ${sym}${ccy !== 'EUR' ? ' (≈ 1 €)' : ''}`,
  };
  const errorsOf = (k) => tripStepErrors(k, f, ctx);
  const err = tried[step] ? errorsOf(step) : {};

  const go = (k) => { setStep(k); top.current?.scrollIntoView?.({ block: 'start' }); };
  const idx = TRIP_STEPS.findIndex((s) => s.key === step);
  const next = () => {
    if (Object.keys(errorsOf(step)).length) {
      setTried((t) => ({ ...t, [step]: true }));
      setTimeout(() => document.querySelector('.pf-card [aria-invalid="true"]')?.focus?.(), 0);
      return;
    }
    go(TRIP_STEPS[idx + 1].key);
  };
  const back = idx > 0 ? () => go(TRIP_STEPS[idx - 1].key) : null;

  const transportLabels = () => f.transports.map((t) => (t === 'autre' ? (f.transportCustom || 'Autre') : TRANSPORTS.find((x) => x.id === t)?.label || t));
  const generate = async (kind) => {
    setAi((a) => ({ ...a, [kind]: true }));
    try {
      if (kind === 'desc') {
        const r = await axios.post(`${API}/ai/trip-description`, {
          origin: f.origCity || f.origCountry, destination: f.destCity || f.destCountry, transports: transportLabels(),
          departure_date: f.date, capacity_kg: f.capacity ? Number(f.capacity) : undefined,
        });
        set('description', (r.data.description ?? '').slice(0, 120));
      } else {
        const r = await axios.post(`${API}/ai/trip-message`, {
          origin: f.origCity, destination: f.destCity, conditions: f.conditions.map((c) => plainLabel(CONDITION_LABELS[c] || c)),
          pricing_mode: f.mode, primary_currency: ccy,
        });
        set('message', (r.data.message ?? '').slice(0, 150));
      }
    } catch {
      toast.error('La rédaction automatique ne répond pas : écrivez quelques mots vous-même.');
    } finally {
      setAi((a) => ({ ...a, [kind]: false }));
    }
  };

  const submit = async () => {
    const bad = firstInvalidStep(TRIP_STEPS, errorsOf);
    if (bad) { setTried((t) => ({ ...t, [bad]: true })); go(bad); return; }
    setLoading(true);
    try {
      const payload = tripPayload(f);
      if (isEdit) {
        await axios.patch(`${API}/trips/${id}`, payload);
        toast.success('Trajet mis à jour — en attente de validation.');
        navigate(`/app/trips/${id}`);
      } else {
        const res = await axios.post(`${API}/trips`, payload);
        clearDraft(DRAFT_KEY);
        toast.success('Trajet publié — en attente de validation.');
        navigate(`/app/trips/${res.data.id}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg || String(d)).join(' ; ') : (detail || 'Erreur lors de la publication'));
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="pf-page"><p className="pf-sub">Chargement…</p></div>;

  const title = isEdit ? 'Modifier le trajet' : isDuplicate ? 'Dupliquer un trajet' : 'Publier un trajet';
  if (!isEdit && user && !canDo(user, 'publish')) {
    return (
      <div className="pf-page">
        <h1 className="pf-h1">{title}</h1>
        <VerificationGate user={user} action="publish" goal="publier un trajet" />
      </div>
    );
  }

  const stepNo = (k) => `Étape ${TRIP_STEPS.findIndex((s) => s.key === k) + 1} sur 4`;
  const cap = Number(String(f.capacity).replace(',', '.'));
  const kgPrice = Number(String(f.pricePerKg).replace(',', '.'));
  const example = f.mode === 'kg' && kgPrice > 0 && cap > 0 ? ` Pour ${fmt(cap, 'EUR')} kg : ${fmt(kgPrice * cap, ccy)} ${sym}.` : '';
  const preview = {
    id: 'apercu', type: 'trajet', from: f.origCity || '…', to: f.destCity || '…',
    price: f.mode === 'kg' ? (kgPrice || null) : f.mode === 'fixe' ? (Number(f.priceFixed) || null) : null,
    unit: f.mode === 'kg' ? `${sym} / kg` : `${sym} / colis`, weight: cap || null, hot: false, daysNum: null,
    user: {
      name: `${user?.first_name || ''} ${(user?.last_name || '').slice(0, 1)}.`.trim(),
      score: user?.avg_rating ? user.avg_rating.toFixed(1).replace('.', ',') : 'Nouveau', trips: user?.rating_count || 0,
      verified: !!user?.is_verified, subscribed: isPro,
    },
    raw: { departure_date: f.date, flex_departure: f.flex, transport_modes: f.transports, pricing_mode: f.mode, author: { first_name: user?.first_name, last_name: user?.last_name } },
  };

  return (
    <div className="pf-page" ref={top}>
      <div>
        <button type="button" className="pf-back" onClick={() => navigate('/app/my-trips')} data-testid="btn-cancel">← Mes trajets</button>
        <h1 className="pf-h1">{title}</h1>
        <p className="pf-sub">
          Quatre étapes, deux minutes. Les expéditeurs de ce corridor pourront vous proposer leurs colis.
          {isDuplicate && ' Formulaire prérempli : adaptez-le avant de publier.'}
        </p>
      </div>
      {draftNote && step === 'route' && (
        <p className="pf-note" data-testid="draft-restored">
          Nous avons gardé ce que vous aviez commencé.{' '}
          <button type="button" className="pf-link" onClick={() => { clearDraft(DRAFT_KEY); setF(EMPTY); setTried({}); setDraftNote(false); }} data-testid="draft-clear">Tout effacer</button>
        </p>
      )}

      <StepBar steps={TRIP_STEPS} current={step} onGo={go} />

      {step === 'route' && (
        <StepCard eyebrow={stepNo('route')} title="Où allez-vous, et quand ?" testid="step-card-route">
          <div className="pf-places">
            <CountryCitySelector dirLabel="Départ" country={f.origCountry} onCountryChange={(v) => set('origCountry', v)}
              city={f.origCity} onCityChange={(v) => set('origCity', v)} errCountry={err.origCountry} errCity={err.origCity} />
            <CountryCitySelector dirLabel="Arrivée" country={f.destCountry} onCountryChange={(v) => set('destCountry', v)}
              city={f.destCity} onCityChange={(v) => set('destCity', v)} errCountry={err.destCountry} errCity={err.destCity} />
          </div>
          <div className="pf-grid">
            <Field id="f-ddep" label="Je pars le" error={err.date} render={(p) => (
              <input {...p} type="date" value={f.date} min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => set('date', e.target.value)} data-testid="input-date-dep" />
            )} />
            <Field id="f-darr" label="J'arrive vers le" optional render={(p) => (
              <input {...p} type="date" value={f.arrivalDate} min={f.date || undefined} onChange={(e) => set('arrivalDate', e.target.value)} />
            )} />
          </div>
          <ChoicePills legend="Cette date est-elle souple ?" options={FLEX_OPTIONS} value={f.flex} onChange={(v) => set('flex', v)}
            testid={(o) => `flex-${o.id}`} />
          <ChoicePills legend="Comment voyagez-vous ?" multi max={3} tiles options={TRANSPORTS} value={f.transports}
            onChange={(v) => set('transports', toggleChoice(f.transports, v, 3))} testid={(o) => `transport-${o.id}`}
            error={err.transports} errorId="err-transports" />
          {f.transports.includes('autre') && (
            <Field id="f-transport-custom" label="Précisez le moyen de transport" error={err.transportCustom} render={(p) => (
              <input {...p} type="text" maxLength={60} placeholder="Moto, camion frigorifique…" value={f.transportCustom}
                onChange={(e) => set('transportCustom', e.target.value)} />
            )} />
          )}
          <StepNav onNext={next} />
        </StepCard>
      )}

      {step === 'space' && (
        <StepCard eyebrow={stepNo('space')} title="La place que vous offrez" testid="step-card-space">
          <div className="pf-grid">
            <div className="pf-field">
              <Field id="f-capacity" label="Place libre en tout" suffix="kg" error={err.capacity} render={(p) => (
                <input {...p} className="pf-input" type="number" inputMode="decimal" step="0.5" min="0.1" max={maxWeight}
                  value={f.capacity} onChange={(e) => set('capacity', e.target.value)} data-testid="input-capacity" />
              )} />
              <p className="pf-hint">
                {isPro ? "Jusqu'à 1 500 kg (abonné PRO)." : (
                  <>Jusqu'à 250 kg, 1 500 kg avec <a className="pro-upsell" href="/app/abonnement" target="_blank" rel="noopener noreferrer" data-testid="trip-form-pro-upsell">l'abonnement PRO</a>.</>
                )}
              </p>
            </div>
            <Field id="f-maxparcel" label="Par colis, au plus" optional suffix="kg" error={err.maxPerParcel} render={(p) => (
              <input {...p} className="pf-input" type="number" inputMode="decimal" step="0.5" min="0.1"
                value={f.maxPerParcel} onChange={(e) => set('maxPerParcel', e.target.value)} />
            )} />
            <Field id="f-nbparcels" label="Nombre de colis" optional render={(p) => (
              <input {...p} type="number" min="1" step="1" value={f.maxParcels} onChange={(e) => set('maxParcels', e.target.value)} />
            )} />
          </div>
          <ChoicePills legend="Ce que vous acceptez" multi max={5} optional options={CATEGORIES.map((c) => ({ id: c, label: c }))}
            value={f.categories} onChange={(v) => set('categories', toggleChoice(f.categories, v))} testid={(o) => `trip-category-${o.id}`} />
          {f.categories.includes('Autre') && (
            <Field id="f-cat-custom" label="Précisez la catégorie" error={err.categoryCustom} render={(p) => (
              <input {...p} type="text" maxLength={60} placeholder="Pièces détachées, jouets…" value={f.categoryCustom}
                onChange={(e) => set('categoryCustom', e.target.value)} />
            )} />
          )}
          {showDims || f.dimL || f.dimW || f.dimH ? (
            <fieldset className="pf-fieldset">
              <legend className="pf-label">Dimensions maximales d'un colis<span className="pf-optional"> (facultatif, en cm)</span></legend>
              <div className="pf-dims">
                {[['dimL', 'Longueur'], ['dimW', 'Largeur'], ['dimH', 'Hauteur']].map(([k, name]) => (
                  <input key={k} className="pf-input" type="number" min="1" placeholder={name} aria-label={`${name} maximale (cm)`}
                    value={f[k]} onChange={(e) => set(k, e.target.value)} />
                ))}
              </div>
            </fieldset>
          ) : (
            <button type="button" className="pf-link pf-link-start" onClick={() => setShowDims(true)}>
              Préciser les dimensions maximales d'un colis
            </button>
          )}
          <StepNav onBack={back} onNext={next} />
        </StepCard>
      )}

      {step === 'price' && (
        <StepCard eyebrow={stepNo('price')} title="Votre tarif" testid="step-card-price">
          <ChoiceCards legend="Comment fixer le prix ?" name="pf-mode" value={f.mode} onChange={(v) => set('mode', v)} testid={(o) => `mode-${o.id}`}
            options={[
              { id: 'kg', title: 'Au kilo', text: 'Selon le poids de chaque colis.' },
              { id: 'fixe', title: 'Forfait par colis', text: 'Le même prix quel que soit le poids.' },
              { id: 'libre', title: 'Libre', text: 'Pas de prix affiché : les expéditeurs vous proposent le leur.' },
            ]} />
          <CurrencyPicker currencies={f.currencies} primary={ccy} error={err.currencies}
            onChange={(list, primary) => setF((x) => ({ ...x, currencies: list, primaryCurrency: primary }))} />
          {f.mode === 'kg' && (
            <div className="pf-field-narrow">
              <Field id="f-price-kg" label="Votre tarif au kilo" suffix={`${sym} / kg`} error={err.pricePerKg}
                hint={<span data-testid="calc-example">Entre {ctx.priceRangeText}.{example}</span>} render={(p) => (
                  <input {...p} className="pf-input" type="number" inputMode="decimal" step="any" min={limits.min}
                    value={f.pricePerKg} onChange={(e) => set('pricePerKg', e.target.value)} data-testid="input-price-kg" />
                )} />
            </div>
          )}
          {f.mode === 'fixe' && (
            <div className="pf-field-narrow">
              <Field id="f-price-fixe" label="Votre forfait par colis" suffix={sym} error={err.priceFixed}
                hint={`${ctx.fixedMinText} au moins.`} render={(p) => (
                  <input {...p} className="pf-input" type="number" inputMode="decimal" step="any" min={limits.min}
                    value={f.priceFixed} onChange={(e) => set('priceFixed', e.target.value)} data-testid="input-price-fixe" />
                )} />
            </div>
          )}
          {f.mode === 'libre' ? (
            <p className="pf-hint">Au tarif libre, chaque expéditeur vous propose son prix : la négociation est toujours ouverte.</p>
          ) : (
            <ChoicePills legend="Les expéditeurs peuvent-ils négocier ?" value={f.counterOffers ? 'open' : 'firm'}
              onChange={(v) => set('counterOffers', v === 'open')} testid={(o) => `negoc-${o.id}`}
              options={[{ id: 'open', label: 'Oui, contre-offres acceptées' }, { id: 'firm', label: 'Non, prix ferme' }]} />
          )}
          <p className="pf-note">Avant d'accepter un colis, vous voyez la commission de Colis2Ship et ce que vous recevrez.</p>
          <StepNav onBack={back} onNext={next} />
        </StepCard>
      )}

      {step === 'details' && (
        <StepCard eyebrow={`${stepNo('details')} · facultative`} title="Les détails qui rassurent" testid="step-card-details">
          <div className="pf-field">
            <div className="pf-row-between">
              <label className="pf-label" htmlFor="f-desc">Description du trajet</label>
              <DraftButton onClick={() => generate('desc')} busy={ai.desc} />
            </div>
            <textarea id="f-desc" className="pf-textarea" rows={3} maxLength={120} value={f.description}
              placeholder="Vol direct, remise possible la veille du départ…" onChange={(e) => set('description', e.target.value.slice(0, 120))}
              data-testid="input-description" />
            <p className="pf-count">{f.description.length}/120</p>
          </div>
          <ChoicePills legend="Vos conditions" multi max={5} options={CONDITIONS} value={f.conditions} visible={6} moreLabel="conditions"
            onChange={(v) => set('conditions', toggleChoice(f.conditions, v))} testid={(o) => `condition-${o.id}`} />
          <div className="pf-field">
            <div className="pf-row-between">
              <label className="pf-label" htmlFor="f-message">Un mot aux expéditeurs</label>
              <DraftButton onClick={() => generate('msg')} busy={ai.msg} />
            </div>
            <textarea id="f-message" className="pf-textarea" rows={2} maxLength={150} value={f.message}
              placeholder="Disponibilités, lieu de remise…" onChange={(e) => set('message', e.target.value.slice(0, 150))} data-testid="input-message" />
            <p className="pf-count">{f.message.length}/150</p>
          </div>
          <StepNav onBack={back} onNext={next} nextLabel="Voir le récapitulatif" onSkip={() => go('recap')} />
        </StepCard>
      )}

      {step === 'recap' && (
        <StepCard eyebrow="Récapitulatif" title="Voici ce que verront les expéditeurs" testid="step-card-recap">
          <div className="pf-recap-card"><ModernAnnonceCard a={preview} preview /></div>
          <RecapLinks steps={TRIP_STEPS} onGo={go} />
          <p className="pf-hint">L'équipe Colis2Ship vérifie chaque annonce avant de la rendre visible.</p>
          <StepNav onBack={back} onNext={submit} busy={loading} nextTestid="btn-submit"
            nextLabel={loading ? 'Publication…' : isEdit ? 'Mettre à jour le trajet' : 'Publier le trajet'} />
        </StepCard>
      )}
    </div>
  );
}
